import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { resources } from "./resources";
import type { SupportedLanguage } from "./resources";

export const DEFAULT_LANGUAGE: SupportedLanguage = "tr";
export const FALLBACK_LANGUAGE: SupportedLanguage = "en";
export const LANGUAGE_STORAGE_KEY = "kareoke.language";
export const SUPPORTED_LANGUAGES = ["tr", "en"] as const satisfies readonly SupportedLanguage[];

export function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return value === "tr" || value === "en";
}

export function normaliseLanguage(value: string | null): SupportedLanguage {
  if (isSupportedLanguage(value)) return value;
  const baseLanguage = value?.split("-")[0] ?? null;
  return isSupportedLanguage(baseLanguage) ? baseLanguage : DEFAULT_LANGUAGE;
}

export function resolveInitialLanguage(): SupportedLanguage {
  /* v8 ignore next -- this frontend module normally runs with a browser window. */
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  try {
    return normaliseLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function updateDocumentLanguage(language: string | null): void {
  /* v8 ignore next -- this frontend module normally runs with a browser document. */
  if (typeof document === "undefined") return;
  document.documentElement.lang = normaliseLanguage(language);
}

function persistLanguage(language: SupportedLanguage): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* v8 ignore next -- persistence is optional when storage is unavailable. */
  }
}

export function reportLanguageChangeFailure(error: unknown): void {
  console.error(resources.en.translation.language.changeError, error);
}

export async function changeKareokeLanguage(
  language: SupportedLanguage,
): Promise<void> {
  persistLanguage(language);
  await i18n.changeLanguage(language);
}

i18n.on("languageChanged", (language) => {
  updateDocumentLanguage(language);
});

export const i18nReady = i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: resolveInitialLanguage(),
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: "translation",
    returnNull: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    updateDocumentLanguage(i18n.language);
  })
  /* v8 ignore next -- init failures depend on i18next internals. */
  .catch(reportLanguageChangeFailure);

export default i18n;
